/* Furniture magnetism (DOM-free): a fixture carried near a wall slides flush
   against the wall's face and turns its back to it — beds, sofas, toilets
   and cabinets all live against walls. ⌥ while placing/dragging disables it. */
import type { Pt, Wall } from './types.ts';
import { distToSeg, wallVec } from './geom.ts';

/** How close (inches) the fixture's back must come to a wall face to snap. */
export const SNAP_REACH = 10;
/** Things that live in the middle of a room — never magnetic. */
export const FREE_STANDING = new Set(['rug', 'island', 'table', 'rtable', 'ctable', 'ottoman', 'plant', 'barstool', 'chair', 'car']);

export interface Pose { x: number; y: number; rot: number; wall: Wall }

/** Pose for a fixture of `size` (w across, h deep; back faces −y at rot 0)
    whose center is at `p`, flush against the nearest wall within reach — or
    null to leave it where it is. `snap` tidies the along-wall position. */
export function snapFixtureToWall(type: string, size: { w: number; h: number }, p: Pt, walls: Wall[],
                                  snap: (v: number) => number = v => v, reach = SNAP_REACH): Pose | null {
  if (FREE_STANDING.has(type)) return null;
  let best: (Pose & { err: number }) | null = null;
  for (const w of walls) {
    const v = wallVec(w);
    const r = distToSeg(p.x, p.y, w.x1, w.y1, w.x2, w.y2);
    if (r.t <= 0 || r.t >= v.len) continue;                      // beside the wall, not past its ends
    const side = (p.x - w.x1) * v.nx + (p.y - w.y1) * v.ny;     // signed distance, + on the left-normal side
    const s = side >= 0 ? 1 : -1;
    const off = w.th / 2 + size.h / 2;                           // center-to-centerline when the back touches the face
    const err = Math.abs(Math.abs(side) - off);
    if (err > reach || (best && err >= best.err)) continue;
    const nx = s * v.nx, ny = s * v.ny;                          // unit normal from the wall toward the fixture
    // the back normal at rotation rot is (sin rot, −cos rot); it must point at the wall: (−nx, −ny)
    const rot = ((Math.round(Math.atan2(-nx, ny) * 180 / Math.PI) % 360) + 360) % 360;
    const t = Math.min(Math.max(snap(r.t), size.w / 2), v.len - size.w / 2);   // whole fixture stays along the wall
    best = { x: w.x1 + v.ux * t + nx * off, y: w.y1 + v.uy * t + ny * off, rot, wall: w, err };
  }
  return best && { x: best.x, y: best.y, rot: best.rot, wall: best.wall };
}
